<template>
  <div>
    <LiveTrackingVehiclePolyline
      v-if="vehicle"
      :vehicle="vehicle"
      :motion-line-data="motionLineData"
    />
    <template v-if="shouldDrawPath">
      <LiveTrackingNextStopPolyline
        v-for="(path, i) in paths"
        :key="`directions-${i}`"
        :vehicle="vehicle"
        :path="path"
      />
    </template>
  </div>
</template>

<script lang="ts">
import { Vue, Prop, Component, Emit } from 'vue-property-decorator'
import { gmapApi } from 'vue2-google-maps'
import LiveTrackingVehiclePolyline from '@/components/LiveTrackingVehiclePolyline.vue'
import LiveTrackingNextStopPolyline from './LiveTrackingNextStopPolyline.vue'
import {
  TrackingVehicle,
  Path,
  MotionLineData,
  Directions,
} from '../models/dto/TrackingVehicle'
import tracking from '@/store/modules/tracking'
import { Coordinates } from '@/models/Coordinates'
import { RouteDirectionsResponse, RouteRequest } from '@/models/Route'
import { SplitFeatureFlag } from '@/utils/enum'
import { AxiosResponse } from 'axios'

@Component({
  components: {
    LiveTrackingVehiclePolyline,
    LiveTrackingNextStopPolyline,
  },
})
export default class LiveTrackingVehicle extends Vue {
  @Prop({ type: Object, required: true }) readonly vehicle: TrackingVehicle

  tracking = tracking
  motionLineData: MotionLineData = {
    legs: [],
  }
  directionsApiDirectionsList: Directions[] = []
  routeApiDirectionsList: RouteDirectionsResponse[] = []

  isDirectionsApiDeprecated = false

  @Emit('directions-service-request')
  emitDirectionsServiceRequest(req: object): object {
    return req
  }

  @Emit('routes-service-request')
  emitRoutesServiceRequest(req: object): object {
    return req
  }

  get google(): any {
    return gmapApi()
  }

  get nextStopLocation(): Path {
    const nextStop = tracking.nextStop
    return {
      lat: nextStop?.address?.lat,
      lng: nextStop?.address?.lng,
    }
  }

  get directionsApiRequestToNextStop(): object {
    return {
      waypoints: [
        { location: this.vehicle.location },
        { location: this.nextStopLocation },
      ],
      origin: this.vehicle.location,
      destination: this.nextStopLocation,
      travelMode: 'DRIVING',
    }
  }

  get routesApiRequestToNextStop(): RouteRequest {
    if (!this.vehicle.location.lat || !this.vehicle.location.lng) {
      return {
        waypoints: [],
        origin: null,
        destination: null,
      }
    }

    return {
      waypoints: [this.vehicle.location as Coordinates, this.nextStopLocation],
      origin: this.vehicle.location as Coordinates,
      destination: this.nextStopLocation,
    }
  }

  get directionsApiRequestFromCurrentLocation(): object {
    return {
      waypoints: this.vehicle.motionPath,
      origin: this.vehicle.motionPath[0],
      destination: this.vehicle.motionPath[this.vehicle.motionPath.length - 1],
      travelMode: 'DRIVING',
    }
  }

  get routesApiRequestFromCurrentLocation(): RouteRequest {
    return {
      waypoints: this.vehicle.motionPath.map((point) => point.location),
      origin: this.vehicle.motionPath[0]?.location,
      destination:
        this.vehicle.motionPath[this.vehicle.motionPath.length - 1]?.location,
    }
  }

  get shouldDrawPath(): boolean {
    const completedStops = tracking.reservation.stops.filter(
      (stop) => stop.isReached
    )
    const allStopsComplete =
      completedStops.length === tracking.reservation.stops.length

    return this.isVehicleBeingTracked || !allStopsComplete
  }

  get isVehicleBeingTracked(): boolean {
    const journeyIds =
      tracking.reservation.reservationIdJourneyIdVehicleIdPairs.flatMap((p1) =>
        p1.idsList.map((id) => id.journeyId)
      )
    return tracking.trackerList && journeyIds.includes(this.vehicle.journeyId)
  }

  get paths(): Coordinates[][] {
    const paths = []

    // TODO: Remove else statement once the Directions API is fully deprecated
    // https://charterup.atlassian.net/browse/CUSERVICE-3395
    if (this.isDirectionsApiDeprecated) {
      for (const directions of this.routeApiDirectionsList) {
        if (!directions?.overviewPath?.length) {
          continue
        }
        paths.push(directions.overviewPath)
      }
    } else {
      for (const directions of this.directionsApiDirectionsList) {
        if (!directions?.routes?.length) {
          continue
        }

        for (const route of directions.routes) {
          if (!!route.overview_path?.length) {
            paths.push(route.overview_path)
          }
        }
      }
    }

    return paths
  }

  async mounted() {
    this.isDirectionsApiDeprecated = await this.$split.isFeatureEnabled(
      SplitFeatureFlag.DirectionsApiDeprecated
    )

    if (this.shouldDrawPath) {
      // TODO: Remove drawPathToNextStopWithDirectionsApi once the Directions API is fully deprecated
      // https://charterup.atlassian.net/browse/CUSERVICE-3395
      if (this.isDirectionsApiDeprecated) {
        this.drawPathToNextStopWithRoutesApi()
      } else {
        this.drawPathToNextStopWithDirectionsApi()
      }
    }
    // TODO: Remove getVehicleMotionLineWithDirectionsApi once the Directions API is fully deprecated
    // https://charterup.atlassian.net/browse/CUSERVICE-3395
    if (this.isDirectionsApiDeprecated) {
      this.getVehicleMotionLineWithRoutesApi()
    } else {
      this.getVehicleMotionLineWithDirectionsApi()
    }
  }

  getVehicleMotionLineWithRoutesApi(): any {
    const routesCallback = (result: AxiosResponse<RouteDirectionsResponse>) => {
      if (!result?.data?.legs?.length) {
        return
      }
      this.motionLineData = this.convertRoutesResponseToMotionLineData(
        result.data
      )
    }

    const req = {
      options: this.routesApiRequestFromCurrentLocation,
      callback: routesCallback,
    }
    this.emitRoutesServiceRequest(req)
  }

  getVehicleMotionLineWithDirectionsApi(): any {
    const directionsCallback = (result, status) => {
      if (status !== 'OK') {
        return
      }
      // check motion line data, if it passes threshold animate, otherwise leave it paused
      this.motionLineData = result.routes[0]
    }

    const req = {
      options: this.directionsApiRequestFromCurrentLocation,
      callback: directionsCallback,
    }
    this.emitDirectionsServiceRequest(req)
  }

  convertRoutesResponseToMotionLineData(
    response: RouteDirectionsResponse
  ): MotionLineData {
    const motionLineData: MotionLineData = {
      legs: [],
    }

    // Convert each leg from RouteDirectionsResponse to MotionLineData format
    for (const routeLeg of response.legs) {
      const motionLeg = {
        distance: {
          value: routeLeg.distance,
        },
        steps: [],
      }

      // Convert each step within the leg
      for (const routeStep of routeLeg.steps) {
        let stepPath = []

        // If we have an encoded polyline, decode it to get the path
        if (routeStep.encodedPolyline) {
          const decodedCoordinates = this.decodePolyline(
            routeStep.encodedPolyline
          )

          // Convert Coordinates to the expected format with lat() and lng() methods
          stepPath = decodedCoordinates.map((coord) => ({
            lat: () => coord.lat,
            lng: () => coord.lng,
          }))
        } else {
          // Fallback: if no polyline, create a simple path from start to end location
          const pathPoints = []
          if (routeStep.startLocation) {
            pathPoints.push({
              lat: () => routeStep.startLocation.lat,
              lng: () => routeStep.startLocation.lng,
            })
          }
          if (routeStep.endLocation) {
            pathPoints.push({
              lat: () => routeStep.endLocation.lat,
              lng: () => routeStep.endLocation.lng,
            })
          }
          stepPath = pathPoints
        }

        const motionStep = {
          path: stepPath,
        }

        motionLeg.steps.push(motionStep)
      }

      motionLineData.legs.push(motionLeg)
    }

    return motionLineData
  }

  decodePolyline(encodedPolyline: string): Array<{ lat: number; lng: number }> {
    if (!this.google || !this.google.maps || !this.google.maps.geometry) {
      console.error('Google Maps geometry library not loaded')
      return []
    }

    const decodedPath =
      this.google.maps.geometry.encoding.decodePath(encodedPolyline)

    // Convert to plain objects if needed
    return decodedPath.map((latLng) => ({
      lat: latLng.lat(),
      lng: latLng.lng(),
    }))
  }

  drawPathToNextStopWithDirectionsApi() {
    const removePreviousLine = this.directionsApiDirectionsList.length > 1
    const directionsCurrent = (directionsData, status) => {
      if (status !== 'OK') {
        return
      }
      this.directionsApiDirectionsList.push(directionsData)
      if (removePreviousLine) {
        this.directionsApiDirectionsList.shift()
      }
    }

    const req = {
      options: this.directionsApiRequestToNextStop,
      callback: directionsCurrent,
    }
    this.emitDirectionsServiceRequest(req)
  }

  drawPathToNextStopWithRoutesApi() {
    const removePreviousLine = this.routeApiDirectionsList.length > 1
    const routesCallback = (result: AxiosResponse<RouteDirectionsResponse>) => {
      if (!result?.data?.legs?.length) {
        return
      }
      this.routeApiDirectionsList.push(result.data)
      if (removePreviousLine) {
        this.routeApiDirectionsList.shift()
      }
    }

    const req = {
      options: this.routesApiRequestToNextStop,
      callback: routesCallback,
    }
    this.emitRoutesServiceRequest(req)
  }
}
</script>
